import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEye, 
  faEyeSlash, 
  faCheck, 
  faExclamationTriangle,
  faSpinner,
  faPaperPlane
} from '@fortawesome/free-solid-svg-icons';
import { publicAPI } from '../../services/api';
import './Auth.css';

export const PasswordReset: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [step, setStep] = useState<'request' | 'confirm' | 'reset' | 'success'>(token ? 'reset' : 'request');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(false);

  useEffect(() => {
    if (token) {
      const validateToken = async () => {
        setLoading(true);
        try {
          await publicAPI.validateToken(token, 'reset');
          setTokenValid(true);
          setStep('reset');
        } catch (err) {
          setError('Token resetowania hasła jest nieprawidłowy lub wygasł.');
          setTokenValid(false);
        } finally {
          setLoading(false);
        }
      };
      validateToken();
    }
  }, [token]);

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await publicAPI.requestPasswordReset(email);
      setStep('confirm');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Wystąpił błąd. Spróbuj ponownie.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Hasła nie są identyczne.');
      return;
    }
    if (password.length < 8) {
      setError('Hasło musi mieć co najmniej 8 znaków.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await publicAPI.resetPassword(token!, password);
      setStep('success');
      setTimeout(() => navigate('/login', { state: { message: 'Hasło zostało zmienione.' } }), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Wystąpił błąd. Spróbuj ponownie.');
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (loading && !token) {
        return (
            <div className="auth-card" style={{maxWidth: '420px', padding: '2rem', textAlign: 'center'}}>
                <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                <h2>Wysyłanie...</h2>
            </div>
        );
    }

    switch (step) {
      case 'request':
        return (
          <form onSubmit={handleRequestReset} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">Adres email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="nazwa@email.com"
              />
            </div>
            {error && <div className="error-message">{error}</div>}
            <button type="submit" disabled={loading} className="auth-button">
              {loading ? 'Wysyłanie...' : 'Wyślij link do resetu hasła'}
            </button>
          </form>
        );
      case 'confirm':
        return (
          <div style={{textAlign: 'center'}}>
            <FontAwesomeIcon icon={faPaperPlane} size="2x" style={{color: 'var(--success-color)', marginBottom: '1rem'}} />
            <h2>Sprawdź swoją skrzynkę</h2>
            <p>Jeśli konto istnieje, wysłaliśmy link do resetowania hasła na adres {email}.</p>
            <Link to="/login" className="auth-link">Powrót do logowania</Link>
          </div>
        );
      case 'reset':
        if (loading && token) {
            return (
                <div className="auth-card" style={{maxWidth: '420px', padding: '2rem', textAlign: 'center'}}>
                    <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                    <h2>Weryfikacja...</h2>
                </div>
            );
        }
        if (!tokenValid) {
            return (
                <div style={{textAlign: 'center'}}>
                    <FontAwesomeIcon icon={faExclamationTriangle} size="2x" style={{color: 'var(--danger-color)', marginBottom: '1rem'}} />
                    <h2>Błąd tokenu</h2>
                    <p>{error}</p>
                    <Link to="/password-reset" className="auth-link">Poproś o nowy link</Link>
                </div>
            );
        }
        return (
          <form onSubmit={handleResetPassword} className="auth-form">
            <div className="form-group">
              <label htmlFor="password">Nowe hasło</label>
              <div className="password-input-group">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  placeholder="Wprowadź nowe hasło"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="password-toggle">
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Potwierdź hasło</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                placeholder="Potwierdź nowe hasło"
              />
            </div>
            {error && <div className="error-message">{error}</div>}
            <button type="submit" disabled={loading} className="auth-button">
              {loading ? 'Zapisywanie...' : 'Zmień hasło'}
            </button>
          </form>
        );
      case 'success':
        return (
          <div style={{textAlign: 'center'}}>
            <FontAwesomeIcon icon={faCheck} size="2x" style={{color: 'var(--success-color)', marginBottom: '1rem'}} />
            <h2>Hasło zmienione!</h2>
            <p>Możesz się teraz zalogować z nowym hasłem.</p>
          </div>
        );
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-split-layout">
        <div className="auth-branding">
          <div className="brand-section">
            <img src="/logo-radonek.png" alt="Radonek Logo" className="auth-logo" />
            <div className="brand-info">
              <h1>Radonek</h1>
              <p className="brand-subtitle">Asystent pacjenta onkologicznego</p>
            </div>
          </div>
          <div className="about-section">
            <h3>Zapomniałeś hasła?</h3>
            <div className="key-features">
              <div className="feature-highlight">
                <div className="feature-icon">📧</div>
                <div className="feature-content">
                  <h4>Sprawdź email</h4>
                  <p>Link do resetu hasła wyślemy na Twój adres email. Będzie ważny przez godzinę.</p>
                </div>
              </div>
              <div className="feature-highlight">
                <div className="feature-icon">�</div>
                <div className="feature-content">
                  <h4>Bezpieczeństwo</h4>
                  <p>Proces jest w pełni bezpieczny. Link może być użyty tylko jeden raz.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="auth-form-wrapper">
          <div className="auth-card">
            <div className="auth-header">
              <div className="form-title">
                <h2>Resetowanie hasła</h2>
                <p className="auth-form-subtitle">
                  {step === 'request' && 'Podaj email, aby zresetować hasło.'}
                  {step === 'reset' && 'Ustaw nowe, silne hasło.'}
                </p>
              </div>
            </div>
            {renderContent()}
            <div className="auth-links">
              <Link to="/login" className="auth-link">
                Pamiętasz hasło? Zaloguj się
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};